import { isMobileDevice } from '~~/utils';

export default function useSocialLogin() {
	const { ORIGIN } = useRuntimeConfig().public;

	const POPUP_WIDTH = 400;
	const POPUP_HEIGHT = 500;

	const screenLeft = computed<number>(() =>
		window?.screenLeft !== undefined ? window?.screenLeft : window?.screenX
	);
	const screenTop = computed<number>(() =>
		window?.screenTop !== undefined ? window?.screenTop : window?.screenY
	);

	const screenWidth = computed<number>(
		() =>
			window?.innerWidth || document.documentElement.clientWidth || screen.width
	);
	const screenHeight = computed<number>(
		() =>
			window?.innerHeight ||
			document.documentElement.clientHeight ||
			screen.height
	);

	const left = computed<number>(
		() => screenLeft.value + (screenWidth.value - POPUP_WIDTH) / 2
	);
	const top = computed<number>(
		() => screenTop.value + (screenHeight.value - POPUP_HEIGHT) / 2
	);

	const isMobile = computed<boolean>(() =>
		isMobileDevice(window?.navigator?.userAgent)
	);

	function handleSubmit(callback: () => void): (provider: string) => void {
		if (import.meta.server) return (_provider: string) => {};

		return (provider: string) => {
			const url = `${ORIGIN}/api/auth/${provider}`;
			const options = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},top=${top.value},left=${left.value}`;

			const popup = isMobile.value
				? window.open(url)
				: window.open(url, 'popup', options);

			if (!popup) return console.error('Failed to open social login popup');

			waitForAuthComplete(popup).then(callback);
		};
	}

	/**
	 * Wait for authentication to complete using postMessage.
	 * Falls back to polling window.closed for backwards compatibility.
	 */
	async function waitForAuthComplete(popup: Window): Promise<void> {
		return new Promise(resolve => {
			let resolved = false;

			// Listen for postMessage from the popup
			function handleMessage(event: MessageEvent) {
				// Verify origin for security
				if (event.origin !== ORIGIN) return;

				if (event.data?.type === 'social-auth-complete') {
					cleanup();
					resolve();
				}
			}

			// Fallback: Poll for popup closure (handles edge cases)
			// Using try-catch to handle COOP restrictions gracefully
			const popupInterval = setInterval(() => {
				try {
					if (popup.closed) {
						cleanup();
						resolve();
					}
				} catch {
					// COOP blocks access to popup.closed - rely on postMessage
				}
			}, 500);

			function cleanup() {
				if (resolved) return;
				resolved = true;
				window.removeEventListener('message', handleMessage);
				clearInterval(popupInterval);
			}

			window.addEventListener('message', handleMessage);

			// Timeout fallback after 5 minutes
			setTimeout(
				() => {
					if (!resolved) {
						cleanup();
						resolve();
					}
				},
				5 * 60 * 1000
			);
		});
	}

	return { handleSubmit };
}
